import { ref, computed } from 'vue';
import axios from 'axios';
import to from 'await-to-js';
import { defineStore } from 'pinia';
import { get, findIndex, keyBy, isNaN, filter } from 'lodash';

import SORT_DIRECTION from '@enums/sort-direction';

const bundleDefaultFields = [
  'name',
  'description',
  'createdBy',
  'creationDate',
  'lastModifiedDate',
  'targetLaunchDate',
];

export const useBundlesStore = defineStore('bundles', () => {
  const bundles = ref([]);
  const selectedBundle = ref(null);
  const archivedBundles = ref([]);
  const archivedBundlesTotalCount = ref(0);
  const archivedBundlesCurrentPage = ref(1);
  const isWorkpackagePanelExpanded = ref(false);
  const loading = ref(false);
  const loadingArchivedBundles = ref(false);

  /**
   * Vuex mutations are still in use
   */

  const updateBundleStateMutation = ({ bundleId, updates }) => {
    const bundleToUpdateIndex = findIndex(bundles.value, b => b._id === bundleId);
    bundles.value[bundleToUpdateIndex] = updates;
  };

  const setArchivedBundlesCurrentPageMutation = page => {
    archivedBundlesCurrentPage.value = page;
  };

  const setSelectedBundleMutation = bundle => {
    selectedBundle.value = bundle;
  };

  const deleteBundleByIdMutation = bundleId => {
    bundles.value = filter(bundles.value, b => b._id !== bundleId);
  };

  const toggleWorkpackagePanelExpandedMutation = ({ bundle }) => {
    if (
      !bundle ||
      ((selectedBundle.value || {})._id === bundle._id && isWorkpackagePanelExpanded.value)
    ) {
      selectedBundle.value = null;
      isWorkpackagePanelExpanded.value = false;
    } else {
      selectedBundle.value = bundle;
      isWorkpackagePanelExpanded.value = true;
    }
  };

  /**
   * Getters
   */

  const getBundleById = computed(
    () => id => [...bundles.value, ...archivedBundles.value].find(el => el._id === id)
  );

  const bundlesById = computed(() => keyBy([...bundles.value, ...archivedBundles.value], '_id'));

  /**
   * Actions
   */

  const fetchBundles = async (customParams = {}) => {
    loading.value = true;

    const pickedFields = bundleDefaultFields.concat(get(customParams, 'pickExtra', []));
    const { limit, offset, regexSearch } = customParams;
    const query = { archived: false };

    let params = {
      sortField: 'creationDate',
      sortDirection: SORT_DIRECTION.ascending,
      pick: pickedFields,
      where: query,
      regexSearch,
    };

    if (!isNaN(limit) && !isNaN(offset)) {
      params = { ...params, ...{ limit, offset } };
    }

    // override defaults
    if (customParams.pick) params.pick = customParams.pick;
    const [err, result] = await to(axios.get('/api/bundles', { params }));
    loading.value = false;
    if (err) throw new Error(err.message);
    const sortedBundles = result.data.bundles.sort((a, b) =>
      a.creationDate.localeCompare(b.creationDate)
    );
    bundles.value = sortedBundles;
  };

  const fetchArchivedBundles = async customParams => {
    loadingArchivedBundles.value = true;

    const pickedFields = [
      ...bundleDefaultFields.concat(get(customParams, 'pickExtra', [])),
      ...['archived', 'archiveDate'],
    ];
    const { limit, offset, regexSearch } = customParams;
    const query = { archived: true };

    let params = {
      sortField: 'creationDate',
      sortDirection: SORT_DIRECTION.ascending,
      pick: pickedFields,
      where: query,
      regexSearch,
    };

    if (!isNaN(limit) && !isNaN(offset)) {
      params = { ...params, ...{ limit, offset } };
    }

    // override defaults
    if (customParams.pick) params.pick = customParams.pick;
    const [err, result] = await to(axios.get('/api/bundles', { params }));
    loadingArchivedBundles.value = false;
    if (err) throw new Error(err.message);
    const sortedBundles = result.data.bundles.sort((a, b) =>
      a.creationDate.localeCompare(b.creationDate)
    );
    archivedBundles.value = sortedBundles;
    archivedBundlesTotalCount.value = result.data.totalCount;
  };

  const updateBundle = async ({ bundleId, updates }) => {
    if (updates._id !== bundleId) {
      throw new Error('mismatched bundleId on update');
    }

    loading.value = true;
    const [err, result] = await to(axios.patch(`/api/bundles/${bundleId}`, updates));
    loading.value = false;
    if (err) {
      const message = get(err, ['response', 'data', 'message'], err.message);
      throw new Error(message);
    }

    if (result) updateBundleStateMutation({ bundleId, updates: result.data.updates });
  };

  return {
    // state
    bundles,
    selectedBundle,
    archivedBundles,
    archivedBundlesTotalCount,
    archivedBundlesCurrentPage,
    isWorkpackagePanelExpanded,
    loading,
    loadingArchivedBundles,
    // mutations
    updateBundleStateMutation,
    setArchivedBundlesCurrentPageMutation,
    setSelectedBundleMutation,
    deleteBundleByIdMutation,
    toggleWorkpackagePanelExpandedMutation,
    // getters
    getBundleById,
    bundlesById,
    // actions
    fetchBundles,
    fetchArchivedBundles,
    updateBundle,
  };
});
